import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';
import { useMutation } from '@/utils/api';

import type { ClientPluginUsage } from './useClientPluginUsage';

interface SetUsageParams {
  client: string;
  plugin: string;
  limit: number;
  headroom: number;
}

interface RunEnrichmentParams {
  client: string;
  targets?: string[];
  config?: Record<string, string>;
}

export function useSetClientUsage() {
  const axios = useAxios({ doNotImpersonate: true });

  return useMutation<ClientPluginUsage, Error, SetUsageParams>({
    defaultErrorMessage: 'Failed to update usage limits',
    mutationFn: async ({ client, plugin, limit, headroom }) => {
      const { data } = await axios.put(`/enrichment/${plugin}`, {
        client,
        limit,
        headroom,
      });
      return data;
    },
    onSuccess: (_data, variables) => {
      toast.success(
        `Updated limits for ${variables.plugin} on ${variables.client}`
      );
      queryClient.invalidateQueries({
        queryKey: ['enrichment', variables.plugin, 'usage', variables.client],
      });
      queryClient.invalidateQueries({
        queryKey: ['enrichment', 'all-usage', variables.client],
      });
    },
  });
}

interface EnrichmentResult {
  succeeded: number;
  failed: number;
  errors: string[];
}

function extractErrorMessage(error: unknown): string {
  const err = error as { response?: { data?: unknown; status?: number } };
  const data = err?.response?.data;
  if (typeof data === 'string' && data) return data;
  if (data && typeof data === 'object' && 'error' in data) {
    const msg = (data as { error: string }).error;
    if (typeof msg === 'string') return msg;
  }
  if (err?.response?.status) return `HTTP ${err.response.status}`;
  return String(error);
}

export function useRunEnrichment() {
  const axios = useAxios({ doNotImpersonate: true });

  return useMutation<EnrichmentResult, Error, RunEnrichmentParams>({
    defaultErrorMessage: 'Failed to run enrichment',
    getErrorFromResponse: true,
    mutationFn: async ({ client, targets, config: extraConfig }) => {
      const headers = { account: client };
      const jobConfig: Record<string, string> = { ...extraConfig };

      if (!targets || targets.length === 0) {
        await axios.post(
          '/job',
          { capabilities: ['pius-discovery'], config: jobConfig },
          { headers }
        );
        return { succeeded: 1, failed: 0, errors: [] };
      }

      const results = await Promise.allSettled(
        targets.map(target =>
          axios.post(
            '/job',
            {
              key: target,
              capabilities: ['pius-discovery'],
              config: jobConfig,
            },
            { headers }
          )
        )
      );

      const errors: string[] = [];
      let succeeded = 0;
      let failed = 0;
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          succeeded++;
        } else {
          failed++;
          const target = targets[i];
          errors.push(`${target}: ${extractErrorMessage(r.reason)}`);
        }
      });

      if (failed > 0 && succeeded === 0) {
        throw new Error(
          errors.length === 1
            ? errors[0]
            : `All ${failed} jobs failed:\n${errors.join('\n')}`
        );
      }

      return { succeeded, failed, errors };
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['enrichment', 'all-usage', variables.client],
      });

      if (data.failed > 0) {
        toast.warning(
          `${data.succeeded} job${data.succeeded !== 1 ? 's' : ''} created, ${data.failed} failed for ${variables.client}`,
          {
            description: data.errors.join('; '),
            duration: 8000,
          }
        );
      } else {
        toast.success(
          `${data.succeeded} enrichment job${data.succeeded !== 1 ? 's' : ''} created for ${variables.client}`
        );
      }
    },
  });
}
